/* useConfirm — AlertDialog unifié, API impérative (spec §9.2).
 *
 * Remplace les confirmations natives du navigateur et les « 2 clics » inline
 * du Nuxt. Comportement repris de `app/components/ConfirmDialog.vue` : focus par
 * défaut sur Annuler, Escape = annuler, clic sur l'overlay = annuler, message
 * en `white-space: pre-line`.
 *
 *   const { confirm } = useConfirm();
 *   if (!await confirm({ title: '…', message: '…', danger: true })) return;
 *
 * L'état est un singleton module : `ConfirmDialogHost.vue`, monté UNE fois
 * dans `App.vue`, en est la seule vue.
 */

import { computed, ref } from 'vue';
import type { ComputedRef } from 'vue';

export interface ConfirmOptions {
  title: string;
  message: string;
  /** Défaut 'Confirmer'. */
  confirmLabel?: string;
  /** Défaut 'Annuler'. */
  cancelLabel?: string;
  /** Défaut false — bouton rouge. */
  danger?: boolean;
}

/** Options une fois les défauts appliqués — ce que rend l'hôte. */
export type ResolvedConfirmOptions = Required<ConfirmOptions>;

const DEFAULT_OPTIONS: ResolvedConfirmOptions = {
  title: '',
  message: '',
  confirmLabel: 'Confirmer',
  cancelLabel: 'Annuler',
  danger: false,
};

const isOpen = ref(false);
const options = ref<ResolvedConfirmOptions>({ ...DEFAULT_OPTIONS });
let resolver: ((confirmed: boolean) => void) | null = null;

/** Ferme le dialogue et tranche la demande en cours (au plus une fois). */
function settle(confirmed: boolean): void {
  isOpen.value = false;
  const resolve = resolver;
  resolver = null;
  resolve?.(confirmed);
}

function confirm(userOptions: ConfirmOptions): Promise<boolean> {
  // Une demande en chasse une autre : la précédente est annulée, jamais orpheline.
  if (resolver) settle(false);
  options.value = { ...DEFAULT_OPTIONS, ...userOptions };
  isOpen.value = true;
  return new Promise<boolean>((resolve) => {
    resolver = resolve;
  });
}

/** Annule la demande en cours — appelé à chaque navigation (le dialogue survit aux pages). */
export function cancelPendingConfirm(): void {
  if (resolver) settle(false);
}

export interface ConfirmApi {
  confirm(options: ConfirmOptions): Promise<boolean>;
}

export interface ConfirmDialogState {
  isOpen: ComputedRef<boolean>;
  options: ComputedRef<ResolvedConfirmOptions>;
  accept(): void;
  cancel(): void;
}

export function useConfirm(): ConfirmApi {
  return { confirm };
}

/**
 * Réservé à `ConfirmDialogHost.vue` : l'état de l'unique dialogue et les deux
 * issues possibles. Aucun autre composant ne l'utilise.
 */
export function useConfirmDialogState(): ConfirmDialogState {
  return {
    isOpen: computed(() => isOpen.value),
    options: computed(() => options.value),
    accept: () => settle(true),
    cancel: () => settle(false),
  };
}
